import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import toast from "react-hot-toast"

import { Pagination } from "components"
import OutletFilterSelect from "components/forms/outlet-filter-select"
import { getDefaultOutletFilterValue, resolveOutletFilter } from "helpers/general-helper"
import useCustomerDebtReportRepositories from "repositories/customer-debt-report"

const formatNumber = (value) => Number(value ?? 0).toLocaleString("id-ID")
const formatCurrency = (value) => Number(value ?? 0).toLocaleString("id-ID", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

const toInputDate = (value) => {
  const date = value ? new Date(value) : new Date()
  if (Number.isNaN(date.getTime())) return ""

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}

const startOfMonth = () => {
  const now = new Date()
  return toInputDate(new Date(now.getFullYear(), now.getMonth(), 1))
}

const today = () => toInputDate(new Date())

const NilaiCell = ({ value }) => {
  const n = Number(value ?? 0)
  const className = n < 0 ? "text-rose-600" : n > 0 ? "text-emerald-700" : "text-neutral-500"
  return <span className={`font-medium ${className}`}>{formatCurrency(value)}</span>
}

export default function List() {
  const [searchParams, setSearchParams] = useSearchParams()

  const dateFrom = searchParams.get("date_from") || startOfMonth()
  const dateTo = searchParams.get("date_to") || today()
  const outletId = searchParams.get("outlet_id") || getDefaultOutletFilterValue()
  const { outlet_id_csv: resolvedOutletCsv } = resolveOutletFilter(outletId)
  const search = searchParams.get("search") || ""
  const page = searchParams.get("page") || "1"
  const perPage = searchParams.get("per_page") || "20"
  const [searchInput, setSearchInput] = useState(search)
  const [expandedKey, setExpandedKey] = useState(null)

  useEffect(() => {
    setSearchInput(search)
  }, [search])

  const submitSearch = () => {
    if (searchInput === search) return
    const nextParams = new URLSearchParams(searchParams)
    if (searchInput.trim() === "") nextParams.delete("search")
    else nextParams.set("search", searchInput.trim())
    nextParams.set("page", "1")
    setSearchParams(nextParams)
  }

  const { data: reportData, isLoading } = useCustomerDebtReportRepositories({
    date_from: dateFrom,
    date_to: dateTo,
    outlet_id: resolvedOutletCsv || undefined,
    search,
    page,
    per_page: perPage,
    enabled: true,
  })

  const rows = useMemo(() => {
    if (Array.isArray(reportData?.items?.data)) return reportData.items.data
    return reportData?.rows ?? []
  }, [reportData])
  const summary = useMemo(() => reportData?.summary ?? {}, [reportData])
  const pagination = reportData?.items

  const paginationLinks = useMemo(() => {
    const links = pagination?.links ?? []
    return links.map((link) => {
      if (!link?.url) return link
      const nextParams = new URLSearchParams(searchParams)
      try {
        const url = new URL(link.url, window.location.origin)
        const pageNumber = url.searchParams.get("page")
        if (pageNumber) nextParams.set("page", pageNumber)
      } catch (error) {
        const urlParams = new URLSearchParams(String(link.url).split("?")[1] ?? "")
        const pageNumber = urlParams.get("page")
        if (pageNumber) nextParams.set("page", pageNumber)
      }
      return { ...link, url: `?${nextParams.toString()}` }
    })
  }, [pagination, searchParams])

  const paginationFrom = useMemo(() => {
    if (pagination?.from) return pagination.from
    if (!pagination || Number(pagination.total ?? 0) === 0) return 0
    return (Number(pagination.current_page ?? 1) - 1) * Number(pagination.per_page ?? perPage) + 1
  }, [pagination, perPage])

  const paginationTo = useMemo(() => {
    if (pagination?.to) return pagination.to
    if (!pagination) return 0
    return Math.min(
      Number(pagination.current_page ?? 1) * Number(pagination.per_page ?? perPage),
      Number(pagination.total ?? 0)
    )
  }, [pagination, perPage])

  const mergeSearchParams = (nextValues) => {
    const nextParams = new URLSearchParams(searchParams)
    Object.entries(nextValues).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        nextParams.delete(key)
      } else {
        nextParams.set(key, value)
      }
    })
    setSearchParams(nextParams)
  }

  const handleExport = () => {
    if (!dateFrom || !dateTo) {
      toast.error("Periode wajib diisi sebelum ekspor")
      return
    }
    const query = new URLSearchParams({
      date_from: dateFrom,
      date_to: dateTo,
      outlet_id: resolvedOutletCsv,
    })
    if (search) query.set("search", search)
    window.open(
      `${process.env.REACT_APP_INVENTORY_API_URL}/reports/customer-debt-report/export?${query.toString()}`,
      "_blank"
    )
  }

  const rowKey = (row, idx) =>
    `${row.customer_id || row.customer_name || "row"}-${idx}`

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 p-5 bg-white border border-neutral-200 rounded-2xl xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h1 className="text-lg font-semibold text-neutral-900">Laporan Sisa Hutang Customer</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Saldo awal + transaksi periode (AI invoice, BC pembayaran) = saldo akhir per customer.
          </p>
        </div>

        <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-end xl:justify-end">
          <div className="min-w-[220px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">Outlet</label>
            <OutletFilterSelect
              value={outletId}
              onChange={(v) => mergeSearchParams({ outlet_id: v, page: "1" })}
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              title="Outlet"
            />
          </div>

          <div className="min-w-[180px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">Cari Customer</label>
            <div className="flex gap-2">
              <input
                type="text"
                className="flex-1 px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitSearch() } }}
                placeholder="Nama / kode customer"
              />
              <button
                type="button"
                onClick={submitSearch}
                className="px-3 py-2 text-sm font-semibold text-white rounded-lg bg-brand-900 hover:bg-brand-800"
              >
                Cari
              </button>
            </div>
          </div>

          <div className="min-w-[160px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">Tanggal Awal</label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              value={dateFrom}
              onChange={(event) => mergeSearchParams({ date_from: event.target.value, page: "1" })}
            />
          </div>

          <div className="min-w-[160px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">Tanggal Akhir</label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              value={dateTo}
              onChange={(event) => mergeSearchParams({ date_to: event.target.value, page: "1" })}
            />
          </div>

          <div className="min-w-[120px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">Per Page</label>
            <select
              value={perPage}
              onChange={(event) => mergeSearchParams({ per_page: event.target.value, page: "1" })}
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
            >
              {[10, 20, 50, 100, 200].map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={handleExport}
            className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-brand-900 hover:bg-brand-800"
          >
            Ekspor Excel
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-5">
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Total Customer</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(summary.total_customers)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Total Saldo Awal</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatCurrency(summary.total_saldo_awal)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Total AI (Invoice)</div>
          <div className="mt-2 text-2xl font-semibold text-emerald-700">{formatCurrency(summary.total_ai)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Total BC (Pembayaran)</div>
          <div className="mt-2 text-2xl font-semibold text-rose-600">{formatCurrency(summary.total_bc)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Total Saldo Akhir</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatCurrency(summary.total_saldo_akhir)}</div>
        </div>
      </div>

      <div className="overflow-hidden bg-white border border-neutral-200 rounded-2xl">
        <div className="px-4 py-4 border-b border-neutral-200 bg-neutral-50">
          <div className="text-sm font-semibold text-neutral-900">{reportData?.report_title || "LAPORAN SISA HUTANG CUSTOMER"}</div>
          <div className="mt-1 text-xs text-neutral-500">{reportData?.period_label || "-"}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-neutral-100">
              <tr>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700 w-10"></th>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">CUSTOMER</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">SALDO AWAL</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">AI (Invoice)</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">BC (Pembayaran)</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">SALDO AKHIR</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-neutral-500">Memuat data laporan...</td>
                </tr>
              )}

              {!isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-neutral-500">
                    Tidak ada customer dengan saldo / transaksi pada periode ini.
                  </td>
                </tr>
              )}

              {!isLoading && rows.map((row, index) => {
                const key = rowKey(row, index)
                const isExpanded = expandedKey === key
                const transaksi = row.transaksi || []

                return (
                  <>
                    <tr key={key} className="border-t border-neutral-200 hover:bg-brand-50 transition-colors">
                      <td className="px-4 py-3">
                        {transaksi.length > 0 ? (
                          <button
                            type="button"
                            onClick={() => setExpandedKey(isExpanded ? null : key)}
                            className="w-6 h-6 inline-flex items-center justify-center text-neutral-600 border rounded hover:bg-neutral-200"
                            title={isExpanded ? "Tutup detail" : "Lihat detail transaksi"}
                          >
                            {isExpanded ? "−" : "+"}
                          </button>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-neutral-800">
                        <div className="font-medium text-neutral-900">{row.customer_name || "-"}</div>
                        <div className="mt-1 text-xs text-neutral-500">
                          {row.customer_id || "-"}
                          {row.customer_phone ? ` • ${row.customer_phone}` : ""}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <NilaiCell value={row.saldo_awal} />
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <NilaiCell value={row.sum_ai} />
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <NilaiCell value={row.sum_bc} />
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap font-semibold">
                        <NilaiCell value={row.saldo_akhir} />
                      </td>
                    </tr>

                    {isExpanded && transaksi.length > 0 && (
                      <tr key={`${key}-detail`} className="bg-neutral-50">
                        <td colSpan={6} className="px-4 py-4">
                          <div className="overflow-x-auto border border-neutral-200 rounded-lg bg-white">
                            <table className="min-w-full text-xs">
                              <thead className="bg-neutral-100">
                                <tr>
                                  <th className="px-3 py-2 font-semibold text-left text-neutral-700 w-12">NO.</th>
                                  <th className="px-3 py-2 font-semibold text-left text-neutral-700">NO FAKTUR</th>
                                  <th className="px-3 py-2 font-semibold text-left text-neutral-700">TGL FAKTUR</th>
                                  <th className="px-3 py-2 font-semibold text-right text-neutral-700">NILAI</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr className="border-t border-neutral-200">
                                  <td className="px-3 py-2" />
                                  <td className="px-3 py-2 italic text-neutral-500" colSpan={2}>Saldo Awal</td>
                                  <td className="px-3 py-2 text-right">
                                    <NilaiCell value={row.saldo_awal} />
                                  </td>
                                </tr>
                                {transaksi.map((trx, trxIdx) => (
                                  <tr key={`${key}-trx-${trxIdx}`} className="border-t border-neutral-200 hover:bg-brand-50 transition-colors">
                                    <td className="px-3 py-2 text-neutral-600">{trxIdx + 1}</td>
                                    <td className="px-3 py-2 text-neutral-900">{trx.no_faktur || "-"}</td>
                                    <td className="px-3 py-2 text-neutral-700">{trx.tgl_faktur || "-"}</td>
                                    <td className="px-3 py-2 text-right">
                                      <NilaiCell value={trx.nilai} />
                                    </td>
                                  </tr>
                                ))}
                                <tr className="border-t border-neutral-200 bg-neutral-50">
                                  <td className="px-3 py-2" />
                                  <td className="px-3 py-2 font-semibold text-neutral-900" colSpan={2}>TOTAL SALDO AKHIR</td>
                                  <td className="px-3 py-2 text-right font-semibold">
                                    <NilaiCell value={row.saldo_akhir} />
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                )
              })}
            </tbody>
            {!isLoading && rows.length > 0 && (
              <tfoot className="bg-neutral-50">
                <tr className="border-t border-neutral-200">
                  <td className="px-4 py-3 font-semibold text-neutral-900" colSpan={2}>GRAND TOTAL</td>
                  <td className="px-4 py-3 font-semibold text-right">
                    <NilaiCell value={summary.total_saldo_awal} />
                  </td>
                  <td className="px-4 py-3 font-semibold text-right">
                    <NilaiCell value={summary.total_ai} />
                  </td>
                  <td className="px-4 py-3 font-semibold text-right">
                    <NilaiCell value={summary.total_bc} />
                  </td>
                  <td className="px-4 py-3 font-semibold text-right">
                    <NilaiCell value={summary.total_saldo_akhir} />
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <div className="px-4 py-4 border-t border-neutral-200">
          <Pagination
            links={paginationLinks}
            from={paginationFrom}
            to={paginationTo}
            total={pagination?.total}
          />
        </div>
      </div>
    </div>
  )
}
